import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Plus, Trash2, Edit, Shield, Search, Filter, Users, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const UsersPage = () => {
  const { api, hasPermission } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [salesCompanies, setSalesCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '', role_id: '', sales_company_id: '' });
  
  // Delete confirmation dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [linkedCustomer, setLinkedCustomer] = useState(null);
  const [deleteLinked, setDeleteLinked] = useState(false);
  const [checkingLinked, setCheckingLinked] = useState(false);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, rolesRes, companiesRes] = await Promise.all([
        api.get('/users/'),
        api.get('/roles/'),
        api.get('/sales-companies/').catch(() => ({ data: [] }))
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
      setSalesCompanies(companiesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filtered users based on search, role, and status
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      // Search filter
      const matchesSearch = searchTerm === '' || 
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Role filter
      const matchesRole = roleFilter === 'all' || user.role_id === roleFilter;
      
      // Status filter
      const matchesStatus = statusFilter === 'all' || 
        (statusFilter === 'active' && user.is_active) ||
        (statusFilter === 'inactive' && !user.is_active);
      
      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [users, searchTerm, roleFilter, statusFilter]);

  const handleAddUser = async () => {
    try {
      const userData = {
        ...newUser,
        sales_company_id: newUser.sales_company_id || null
      };
      await api.post('/users/', userData);
      toast.success('User created successfully');
      setShowAddDialog(false);
      setNewUser({ email: '', password: '', name: '', role_id: '', sales_company_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleUpdateRole = async (userId, roleId) => {
    try {
      await api.put(`/users/${userId}/role?role_id=${roleId}`);
      toast.success('User role updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleToggleStatus = async (userId, isActive) => {
    try {
      await api.put(`/users/${userId}/status?is_active=${isActive}`);
      toast.success(`User ${isActive ? 'activated' : 'deactivated'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Check for linked customer before deleting
  const handleDeleteClick = async (user) => {
    setUserToDelete(user);
    setDeleteLinked(false);
    setLinkedCustomer(null);
    
    // Check if user has a linked customer record
    if (user.role_id === 'role_customer') {
      setCheckingLinked(true);
      try {
        const response = await api.get(`/users/${user.id}/linked-customer`);
        if (response.data.has_linked_customer) {
          setLinkedCustomer(response.data.customer);
        }
      } catch (error) {
        console.error('Error checking linked customer:', error);
      } finally {
        setCheckingLinked(false);
      }
    }
    
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    
    try {
      await api.delete(`/users/${userToDelete.id}?delete_linked=${deleteLinked}`);
      toast.success(deleteLinked && linkedCustomer 
        ? 'User and linked customer deleted' 
        : 'User deleted');
      fetchData();
      setShowDeleteDialog(false);
      setUserToDelete(null);
      setLinkedCustomer(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setRoleFilter('all');
    setStatusFilter('all');
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Users</h1>
            <p className="text-gray-500 dark:text-gray-400">Manage system users</p>
          </div>
          {hasPermission('perm_users_create') && (
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button className="bg-[#e85a2c] hover:bg-[#d14a1e]">
                  <Plus className="w-4 h-4 mr-2" />
                  Add User
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New User</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Full Name</Label>
                    <Input
                      value={newUser.name}
                      onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="john@example.com"
                    />
                  </div>
                  <div>
                    <Label>Password</Label>
                    <Input
                      type="password"
                      value={newUser.password}
                      onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                      placeholder="••••••••"
                    />
                  </div>
                  <div>
                    <Label>Role</Label>
                    <Select
                      value={newUser.role_id}
                      onValueChange={(value) => setNewUser(prev => ({ ...prev, role_id: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a role" />
                      </SelectTrigger>
                      <SelectContent>
                        {roles.map(role => (
                          <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Sales Company (Optional)</Label>
                    <Select
                      value={newUser.sales_company_id || 'none'}
                      onValueChange={(value) => setNewUser(prev => ({ ...prev, sales_company_id: value === 'none' ? '' : value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a company" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Company (Platform User)</SelectItem>
                        {salesCompanies.map(company => (
                          <SelectItem key={company.id} value={company.id}>{company.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">Assign user to a sales company for tenant-specific access</p>
                  </div>
                  <Button onClick={handleAddUser} className="w-full bg-[#e85a2c] hover:bg-[#d14a1e]">
                    Create User
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Filters Section */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="user-search-input"
              />
            </div>
            
            {/* Role Filter */}
            <div className="min-w-[180px]">
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger data-testid="role-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  {roles.map(role => (
                    <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Status Filter */}
            <div className="min-w-[150px]">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Clear Filters */}
            {(searchTerm || roleFilter !== 'all' || statusFilter !== 'all') && (
              <Button variant="outline" size="sm" onClick={clearFilters}>
                Clear Filters
              </Button>
            )}
          </div>
          
          {/* Filter Summary */}
          <div className="mt-3 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Users className="w-4 h-4" />
            <span>Showing {filteredUsers.length} of {users.length} users</span>
            {roleFilter !== 'all' && (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">
                Role: {roles.find(r => r.id === roleFilter)?.name}
              </span>
            )}
            {statusFilter !== 'all' && (
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                statusFilter === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                {statusFilter}
              </span>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="text-left py-4 px-6 text-gray-600 dark:text-gray-300 font-medium">User</th>
                  <th className="text-left py-4 px-6 text-gray-600 dark:text-gray-300 font-medium">Email</th>
                  <th className="text-left py-4 px-6 text-gray-600 dark:text-gray-300 font-medium">Role</th>
                  <th className="text-left py-4 px-6 text-gray-600 dark:text-gray-300 font-medium">Status</th>
                  <th className="text-left py-4 px-6 text-gray-600 dark:text-gray-300 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-gray-500 dark:text-gray-400">
                      {users.length === 0 ? 'No users found' : 'No users match the current filters'}
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.id} className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#e85a2c] rounded-full flex items-center justify-center">
                            <span className="text-white font-semibold">
                              {user.name?.charAt(0)?.toUpperCase()}
                            </span>
                          </div>
                          <span className="font-medium text-gray-900 dark:text-white">{user.name}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-gray-600 dark:text-gray-300">{user.email}</td>
                      <td className="py-4 px-6">
                        {hasPermission('perm_users_update') ? (
                          <Select
                            value={user.role_id || ''}
                            onValueChange={(value) => handleUpdateRole(user.id, value)}
                          >
                            <SelectTrigger className="w-[160px]">
                              <Shield className="w-4 h-4 mr-2" />
                              <SelectValue placeholder="Select role" />
                            </SelectTrigger>
                            <SelectContent>
                              {roles.map(role => (
                                <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                            <Shield className="w-4 h-4 text-gray-400" />
                            {user.role_name || 'No role'}
                          </span>
                        )}
                      </td>
                      <td className="py-4 px-6">
                        <button
                          onClick={() => hasPermission('perm_users_update') && handleToggleStatus(user.id, !user.is_active)}
                          className={`px-3 py-1 rounded-full text-sm font-medium cursor-pointer ${
                            user.is_active
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {user.is_active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="py-4 px-6">
                        {hasPermission('perm_users_delete') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(user)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Delete User
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Are you sure you want to delete <strong>{userToDelete?.name}</strong> ({userToDelete?.email})?
            </p>
            
            {checkingLinked && (
              <p className="text-sm text-gray-500">Checking for linked records...</p>
            )}
            
            {linkedCustomer && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                  This user has a linked Customer record:
                </p>
                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                  {linkedCustomer.first_name} {linkedCustomer.last_name} - {linkedCustomer.email}
                </p>
                <label className="flex items-center gap-2 mt-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={deleteLinked}
                    onChange={(e) => setDeleteLinked(e.target.checked)}
                    className="w-4 h-4 rounded border-yellow-400 text-yellow-600 focus:ring-yellow-500"
                  />
                  <span className="text-sm text-yellow-800 dark:text-yellow-200">
                    Also delete from Customer Management
                  </span>
                </label>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleConfirmDelete}
              disabled={checkingLinked}
            >
              {deleteLinked ? 'Delete Both' : 'Delete User Only'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default UsersPage;
